import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import useReveal from "../../hooks/useReveal";
import { BDK, waUrl } from "../../config/bdk";
import { getCookie } from "../../lib/fbCookies";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  nombre: "",
  whatsapp: "",
  correo: "",
  ciudad: "",
  negocio: "",
  giro: "",
  servicio: "Landing page",
  presupuesto: "No estoy seguro",
  objetivo: "",
  acepta_contacto: true,
};

const servicios = [
  "Landing page",
  "Sitio web empresarial",
  "Catálogo / citas / reservaciones",
  "Tienda en línea",
  "Desarrollo a medida",
  "No estoy seguro, necesito orientación",
];

const presupuestos = [
  "Menos de $4,000 MXN",
  "$4,000 a $7,000 MXN",
  "$7,000 a $15,000 MXN",
  "Más de $15,000 MXN",
  "No estoy seguro",
];

const inputCls =
  "w-full bg-white text-[13px] text-[#111218] px-3.5 py-3 focus:outline-none transition-all placeholder:text-[#98a1af]";

const inputWrapperCls =
  "rounded-[8px] bg-white transition-all focus-within:border-[#d90020]";

const ContactForm = () => {
  useReveal();
  const [form, setForm] = useState(initialState);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const recaptchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const startTrackedRef = useRef(false);

  const trackFormStart = () => {
    if (startTrackedRef.current) return;
    startTrackedRef.current = true;
    window.fbq?.("trackCustom", "InicioFormulario");
  };

  useEffect(() => {
    // The recaptcha script is loaded with `render=explicit` (see index.html)
    // specifically to avoid Google's implicit auto-render, which races
    // against React mounting this div and silently renders nothing if it
    // loses. Poll until the API is ready, then render explicitly ourselves.
    let cancelled = false;
    const tryRender = () => {
      if (cancelled || widgetIdRef.current !== null) return;
      if (window.grecaptcha?.render && recaptchaRef.current) {
        widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
          sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        });
      } else {
        setTimeout(tryRender, 200);
      }
    };
    tryRender();
    return () => {
      cancelled = true;
    };
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.acepta_contacto) {
      toast.error("Debes autorizar el contacto para enviar la solicitud.");
      return;
    }
    if (!form.nombre || !form.whatsapp || !form.correo || !form.servicio) {
      toast.error("Por favor completa los campos obligatorios.");
      return;
    }
    const recaptchaToken = window.grecaptcha?.getResponse?.(widgetIdRef.current);
    if (!recaptchaToken) {
      toast.error("Por favor completa la verificación anti-spam.");
      return;
    }
    const eventId = crypto.randomUUID();
    try {
      setSending(true);
      const res = await axios.post(
        `${API}/leads`,
        {
          ...form,
          recaptcha_token: recaptchaToken,
          event_id: eventId,
          fbp: getCookie("_fbp"),
          fbc: getCookie("_fbc"),
          event_source_url: window.location.href,
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );
      if (res?.data?.id) {
        setSent(true);
        toast.success("Gracias. Recibimos tu solicitud.");
        // eventID dedupes this browser-side Lead against the server-side
        // Conversions API Lead sent for the same submission (see backend).
        window.fbq?.(
          "track",
          "Lead",
          { value: 2, currency: "MXN", lead_type: "formulario" },
          { eventID: eventId }
        );
        window.fbq?.("trackCustom", "EnvioFormulario");
        setForm(initialState);
      } else {
        throw new Error("Respuesta inválida");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        "No pudimos enviar tu solicitud. Intenta nuevamente o contáctanos por WhatsApp."
      );
    } finally {
      window.grecaptcha?.reset?.(widgetIdRef.current);
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="section-contacto"
      className="bdk-section"
      style={{ background: "var(--paper)" }}
    >
      <div className="container-bdk">
        <div className="grid lg:grid-cols-[.85fr_1.15fr] gap-6 lg:gap-10">
          {/* Left - Trust */}
          <div
            className="p-8 md:p-10 rounded-[14px] text-white bdk-reveal bdk-reveal--left"
            style={{ background: "var(--ink)", "--reveal-delay": "0ms" }}
          >
            <p className="kicker">Solicita tu propuesta</p>
            <h2 className="font-display text-[34px] md:text-[44px] leading-none">
              Cuéntanos de tu negocio
              <br />y te orientamos.
            </h2>

            <ul className="mt-7 space-y-2.5 text-[13px] md:text-[14px] text-white/90">
              {[
                "Revisamos tu negocio antes de proponer nada.",
                "Te orientamos sobre qué tipo de página te conviene.",
                "Puedes ver una propuesta visual inicial si aplica.",
                "Atención por WhatsApp.",
                "Respuesta en menos de 24 horas hábiles.",
              ].map((b, i) => (
                <li key={i} className="relative pl-5">
                  <span
                    className="absolute left-0 top-0 font-black"
                    style={{ color: "var(--red)" }}
                  >
                    ✓
                  </span>
                  {b}
                </li>
              ))}
            </ul>

            <div
              className="mt-7 p-5 rounded-[10px]"
              style={{
                background: "var(--ink-2)",
                borderLeft: "4px solid var(--red)",
              }}
            >
              <p className="text-[13px] md:text-[14px] text-white leading-relaxed">
                <b>No necesitas contratar de inmediato.</b> Primero revisamos tu
                caso y te decimos qué opción conviene para tu negocio.
              </p>
            </div>

            <p className="mt-6 text-[11px] text-[#b8b9c1] leading-relaxed">
              La propuesta visual inicial aplica para landing pages y páginas
              sencillas. Proyectos avanzados requieren cotización personalizada.
            </p>

            <a
              href={waUrl()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-cta"
              className="bdk-btn bdk-btn--whatsapp mt-7"
            >
              Prefiero WhatsApp
            </a>
          </div>

          {/* Right - Form */}
          <div
            className="p-6 md:p-10 rounded-[14px] bg-white bdk-reveal bdk-reveal--right"
            style={{
              border: "1px solid var(--line)",
              boxShadow: "0 10px 40px rgba(17,18,24,.05)",
              "--reveal-delay": "80ms",
            }}
          >
            {sent ? (
              <div
                data-testid="contact-success"
                className="min-h-[400px] flex flex-col items-center justify-center text-center"
              >
                <div
                  className="w-16 h-16 rounded-[12px] grid place-items-center text-white text-2xl font-black"
                  style={{ background: "var(--red)" }}
                >
                  ✓
                </div>
                <h3 className="mt-6 font-display text-[32px] leading-none">
                  ¡Solicitud recibida!
                </h3>
                <p className="mt-3 text-[#666873] max-w-md text-[14px]">
                  Gracias. Recibimos tu solicitud. Te contactaremos en menos de
                  24 horas hábiles para revisar tu proyecto.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href={waUrl(
                      "Hola, acabo de enviar el formulario en la página de BDKSISTEMAS y quiero adelantar mi solicitud por WhatsApp."
                    )}
                    target="_blank"
                    rel="noreferrer"
                    data-no-lead-modal="true"
                    data-testid="contact-success-whatsapp"
                    className="bdk-btn bdk-btn--whatsapp"
                  >
                    Adelantar por WhatsApp
                  </a>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="contact-new-request"
                    className="bdk-btn bdk-btn--outline"
                  >
                    Enviar otra solicitud
                  </button>
                </div>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={onSubmit}
                onFocus={trackFormStart}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <Field label="Nombre completo *" name="nombre">
                  <input
                    required
                    data-testid="input-nombre"
                    name="nombre"
                    value={form.nombre}
                    onChange={onChange}
                    placeholder="Tu nombre"
                    className={inputCls}
                    style={{ border: "1px solid var(--line)", borderRadius: 8 }}
                  />
                </Field>
                <Field label="WhatsApp *" name="whatsapp">
                  <input
                    required
                    data-testid="input-whatsapp"
                    name="whatsapp"
                    value={form.whatsapp}
                    onChange={onChange}
                    placeholder="+52 81 1234 5678"
                    className={inputCls}
                    style={{ border: "1px solid var(--line)", borderRadius: 8 }}
                  />
                </Field>
                <Field label="Correo electrónico *" name="correo">
                  <input
                    required
                    type="email"
                    data-testid="input-correo"
                    name="correo"
                    value={form.correo}
                    onChange={onChange}
                    placeholder="tucorreo@ejemplo.com"
                    className={inputCls}
                    style={{ border: "1px solid var(--line)", borderRadius: 8 }}
                  />
                </Field>
                <Field label="Ciudad (opcional)" name="ciudad">
                  <input
                    data-testid="input-ciudad"
                    name="ciudad"
                    value={form.ciudad}
                    onChange={onChange}
                    placeholder="Ciudad, estado"
                    className={inputCls}
                    style={{ border: "1px solid var(--line)", borderRadius: 8 }}
                  />
                </Field>
                <Field label="Nombre del negocio (opcional)" name="negocio">
                  <input
                    data-testid="input-negocio"
                    name="negocio"
                    value={form.negocio}
                    onChange={onChange}
                    placeholder="Ej. Cafetería El Roble"
                    className={inputCls}
                    style={{ border: "1px solid var(--line)", borderRadius: 8 }}
                  />
                </Field>
                <Field label="Giro del negocio (opcional)" name="giro">
                  <input
                    data-testid="input-giro"
                    name="giro"
                    value={form.giro}
                    onChange={onChange}
                    placeholder="Ej. Restaurante, gimnasio…"
                    className={inputCls}
                    style={{ border: "1px solid var(--line)", borderRadius: 8 }}
                  />
                </Field>
                <Field label="Servicio que te interesa *" name="servicio">
                  <select
                    required
                    data-testid="select-servicio"
                    name="servicio"
                    value={form.servicio}
                    onChange={onChange}
                    className={inputCls}
                    style={{ border: "1px solid var(--line)", borderRadius: 8 }}
                  >
                    {servicios.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Presupuesto aproximado (opcional)" name="presupuesto">
                  <select
                    data-testid="select-presupuesto"
                    name="presupuesto"
                    value={form.presupuesto}
                    onChange={onChange}
                    className={inputCls}
                    style={{ border: "1px solid var(--line)", borderRadius: 8 }}
                  >
                    {presupuestos.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="¿Qué necesitas lograr? (opcional)"
                  name="objetivo"
                  full
                >
                  <textarea
                    data-testid="input-objetivo"
                    name="objetivo"
                    value={form.objetivo}
                    onChange={onChange}
                    rows={3}
                    placeholder="Cuéntanos qué esperas de la página, tu meta principal o cualquier detalle extra…"
                    className={inputCls}
                    style={{
                      border: "1px solid var(--line)",
                      borderRadius: 8,
                      resize: "vertical",
                    }}
                  />
                </Field>

                <label className="md:col-span-2 flex items-start gap-3 text-[12px] text-[#666873] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="checkbox-acepta"
                    name="acepta_contacto"
                    checked={form.acepta_contacto}
                    onChange={onChange}
                    className="mt-0.5 w-4 h-4"
                    style={{ accentColor: "#d90020" }}
                  />
                  <span>
                    Autorizo a <b>{BDK.companyLegalName}</b> a contactarme por
                    WhatsApp o correo para dar seguimiento a esta solicitud.
                    Consulta nuestro{" "}
                    <a
                      href="/aviso-de-privacidad.html"
                      target="_blank"
                      rel="noreferrer"
                      data-testid="contact-privacy-link"
                      className="underline font-semibold"
                      style={{ color: "var(--red)" }}
                    >
                      Aviso de privacidad
                    </a>
                    .
                  </span>
                </label>

                <div
                  ref={recaptchaRef}
                  data-testid="recaptcha-slot"
                  className="md:col-span-2"
                />

                <div className="md:col-span-2 flex flex-col sm:flex-row items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    className="bdk-btn bdk-btn--primary bdk-btn--lg w-full sm:w-auto disabled:opacity-70"
                  >
                    {sending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Enviando…
                      </>
                    ) : (
                      "Enviar solicitud"
                    )}
                  </button>
                  <p className="text-[11px] text-[#666873]">
                    Respuesta en menos de 24h hábiles.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label
      htmlFor={name}
      className="block text-[10px] font-black uppercase tracking-widest text-[#666873] mb-1.5"
    >
      {label}
    </label>
    {children}
  </div>
);

export default ContactForm;
